import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { CodeBracketIcon } from '@heroicons/react/24/outline';

const JSONProcessorNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const operation = data.config?.operation || 'parse';

  const getOperationIcon = (op: string) => {
    const icons: Record<string, string> = {
      parse: '📥', // String -> Object
      stringify: '📤', // Object -> String
      validate: '✅', // Check if valid JSON
      extract: '🔍', // Extract value by path
      merge: '🔗', // Merge objects
      keys: '🗝️', // Get object keys
      values: '📋', // Get object values
      pretty: '✨', // Pretty format JSON
      minify: '📦', // Minify JSON
      transform: '🔄' // Transform structure
    };
    return icons[op] || '📝';
  };

  const getOperationLabel = (op: string) => {
    const labels: Record<string, string> = {
      parse: 'JSON elemzés',
      stringify: 'String-gé alakítás',
      validate: 'Érvényesség ellenőrzés',
      extract: 'Érték kinyerés',
      merge: 'Objektumok egyesítése',
      keys: 'Kulcsok lekérdezése',
      values: 'Értékek lekérdezése',
      pretty: 'Szép formázás',
      minify: 'Tömörítés',
      transform: 'Struktúra átalakítás'
    };
    return labels[op] || op;
  };

  const getOperationColor = (op: string) => {
    switch (op) {
      case 'parse': return 'from-slate-400 to-slate-500';
      case 'stringify': return 'from-slate-500 to-slate-600';
      case 'validate': return 'from-green-400 to-green-500';
      case 'extract': return 'from-blue-400 to-blue-500';
      case 'merge': return 'from-purple-400 to-purple-500';
      default: return 'from-slate-400 to-slate-500';
    }
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r ${getOperationColor(operation)} border-2 transition-all duration-200 ${
      selected ? 'border-slate-600 shadow-xl scale-105' : 'border-slate-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <CodeBracketIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-slate-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Operation display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-sm">
              {getOperationIcon(operation)}
            </span>
            <span className="text-xs text-slate-100">
              {getOperationLabel(operation)}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* JSON processing result preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-slate-100 bg-black bg-opacity-20 px-2 py-1 rounded max-h-24 overflow-y-auto">
          <strong>Eredmény:</strong>
          <pre className="whitespace-pre-wrap mt-1 text-xs">
            {typeof data.executionData.result === 'string' 
              ? data.executionData.result.length > 100 
                ? data.executionData.result.substring(0, 100) + '...'
                : data.executionData.result
              : JSON.stringify(data.executionData.result, null, 2).substring(0, 200)
            }
          </pre>
        </div>
      )}

      {/* Configuration preview */}
      {data.config?.path && (
        <div className="mt-2 text-xs text-slate-200 bg-slate-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Útvonal:</strong> {data.config.path}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-slate-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-slate-600 border-2 border-white"
      />
    </div>
  );
};

export default JSONProcessorNode;
